// deps
import React, { memo, useCallback, useMemo, useContext } from "react";

// internal deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { AddMoreItems, NoItems } from "@swiggy-private/cart-ui";
import { ApiMenuCartItem, GroupOrderRestaurantDetails, UserSpecificDetails } from "@swiggy-private/cart-types";
import { GROUP_CART_ORDER_DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { format, isEmpty } from "@swiggy-private/js-utils";
import {
    closeWebview,
    getBridgeSdk,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    GroupOrderCartContext,
} from "@swiggy-private/checkout-core";
import { NavigationTypes } from "@swiggy-private/native-bridge";

// components
import { CartItemRenderer } from "@cart-client/components/cart-item-renderer";

// constants
import { GROUP_ORDER_MENU_DEEPLINK, MY_ITEMS, USER_NAME_WITH_HOST } from "@cart-client/constants/GroupOrder";

// styles
import { AddMoreItemsContainer, Container, ContainerWrapper, MainContainer } from "./GroupCartRenderer.style";

// helpers
import {
    getHostFirstName,
    getUserFullName,
    transformGroupCartResponseToCartItemsListProps,
} from "@cart-client/helpers/GroupCartOrder";

interface CartGroupOrderItemProps {
    groupCartId: string;
    isCurrentItemFromSameUser: boolean;
    position: number;
    userDetails: UserSpecificDetails;
    isHost: boolean;
    hostUserId?: string;
    updateGroupCart: (cartItems: ApiMenuCartItem[]) => void;
    impressionGrpCartItem: (type: string, position: number, userId: string, itemIds?: string[]) => void;
    clickGrpCartItemEvent: (label: string) => void;
}

const ADD_MORE_ITEMS_LABEL = "add-more-items";

const CartGroupOrderItem = ({
    groupCartId,
    position,
    isCurrentItemFromSameUser,
    userDetails,
    isHost,
    hostUserId,
    updateGroupCart,
    impressionGrpCartItem,
    clickGrpCartItemEvent,
}: CartGroupOrderItemProps) => {
    const { state } = useContext(GroupOrderCartContext);
    const { data: groupCartData } = state;

    const isFromBottomBar = GlobalHeaderState.getHeader(SwgyHeaderKeysList.isFromBottomBar) === "true";

    const currentUserId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId);

    const restaurantDetails = groupCartData?.restaurant_details;

    const impressionAddMoreItems = useCallback(() => {
        impressionGrpCartItem(ADD_MORE_ITEMS_LABEL, 1, currentUserId);
    }, [currentUserId, impressionGrpCartItem]);

    const handleAddMoreItems = useCallback(() => {
        clickGrpCartItemEvent(ADD_MORE_ITEMS_LABEL);

        // Case: when user is coming from home page crouton we need to take him to group order menu page directly,
        // this can not be done by normal deeplink navigation for menu page, since with group order, there are 2 variants of menu page.
        if (isFromBottomBar) {
            const restaurantId = groupCartData?.restaurant_details?.restaurant_id || "";
            getBridgeSdk()
                .navigateToScreen({
                    type: NavigationTypes.DEEPLINK,
                    link: format(GROUP_ORDER_MENU_DEEPLINK, restaurantId, groupCartId, getHostFirstName(groupCartData)),
                    params: {
                        shouldCloseWebview: true,
                    },
                })
                .catch(() => void 0);
            return;
        }
        closeWebview();
    }, [clickGrpCartItemEvent, isFromBottomBar, groupCartData, groupCartId]);

    const userName = isCurrentItemFromSameUser ? MY_ITEMS : getUserFullName(userDetails);
    const nameWithUserType =
        !isCurrentItemFromSameUser && hostUserId == userDetails.user_details.user_id
            ? format(USER_NAME_WITH_HOST, userName)
            : userName;

    const transformedGroupCartResponse = useMemo(() => {
        return transformGroupCartResponseToCartItemsListProps(
            userDetails,
            restaurantDetails as GroupOrderRestaurantDetails,
            {
                isHost,
                groupCartId,
                isGroupOrderFlow: true,
                isCurrentItemFromSameUser,
            },
        );
    }, [groupCartId, isCurrentItemFromSameUser, isHost, restaurantDetails, userDetails]);

    useEffectOnce(() => {
        const itemIds = userDetails.user_cart.map(cartItem => String(cartItem.menu_item_id));
        impressionGrpCartItem("cart-item", position + 1, userDetails.user_details.user_id, itemIds);
    });
    const areItemsEmpty = isEmpty(userDetails?.user_cart);

    return (
        <MainContainer
            data-testid={`${GROUP_CART_ORDER_DATA_TEST_IDS.ITEMS_CONTAINER}-${userDetails.user_details.user_id}`}
        >
            <Text variant="V2.body1.neutral.bold">{nameWithUserType}</Text>
            <ContainerWrapper isCurrentItemFromSameUser={isCurrentItemFromSameUser}>
                <Container>
                    {areItemsEmpty ? (
                        <NoItems isCurrentItemFromSameUser={isCurrentItemFromSameUser} />
                    ) : (
                        <CartItemRenderer {...transformedGroupCartResponse} updateGroupCart={updateGroupCart} />
                    )}

                    {isCurrentItemFromSameUser && (
                        <AddMoreItemsContainer onClick={handleAddMoreItems}>
                            <AddMoreItems impressionEvent={impressionAddMoreItems} areItemsEmpty={areItemsEmpty} />
                        </AddMoreItemsContainer>
                    )}
                </Container>
            </ContainerWrapper>
        </MainContainer>
    );
};

export default memo(CartGroupOrderItem);
